import React, { useCallback } from 'react';

import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ClassesContainer from './ClassesContainer';
import ClassFilters from './ClassFilters';
import ClassTableRow from './ClassTableRow';
import { DownloadChatModal } from './DownloadChat/DownloadChatModal';
import LoadingTableRows from './LoadingTableRows';
import { Direction, DirectionOption, SortBy, SortOption } from './types';
import { PaginationContainer, TablePagination, useTablePagination } from '../../../../components/TablePagination';
import { Status } from '../../../../types/apiTypes';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

export interface ClassesTableBodyProps {}

const ClassesTableBody: React.FC<ClassesTableBodyProps> = () => {
    const {
        uuid,
        status,
        sortBy,
        setSortBy,
        sortDirection,
        selectedClass,
        filteredClasses,
        setSortDirection,
        setSelectedClass
    } = useContainer(ClassesContainer);

    const { t } = useTranslation('admin_settings');

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredClasses, 25);

    const handleSortSelection = useCallback(
        ({ direction, _sortBy }: { direction: Direction; _sortBy: SortBy }) => {
            if (sortBy !== _sortBy) {
                setSortDirection(DirectionOption.ASCENDING);
                setSortBy(_sortBy);
                return;
            }

            setSortBy(_sortBy);

            if (!direction) setSortDirection(DirectionOption.ASCENDING);
            if (direction === DirectionOption.ASCENDING) setSortDirection(DirectionOption.DESCENDING);
            if (direction === DirectionOption.DESCENDING) setSortDirection(undefined);
        },
        [setSortBy, setSortDirection, sortBy]
    );

    return (
        <>
            <ClassFilters setPage={setPage} />

            <StyledTable>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Tooltip arrow title={t('classes.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.NAME && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({
                                            direction: sortDirection,
                                            _sortBy: SortOption.NAME
                                        })
                                    }
                                >
                                    {t('classes.name-with-count', { classCount: filteredClasses.length })}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>
                            <Tooltip arrow title={t('classes.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.INSTRUCTOR && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({
                                            direction: sortDirection,
                                            _sortBy: SortOption.INSTRUCTOR
                                        })
                                    }
                                >
                                    {t('classes.instructor')}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>
                            <Tooltip arrow title={t('classes.sort')}>
                                <TableSortLabel
                                    active={sortBy === SortOption.DATES && !!sortDirection}
                                    direction={sortDirection}
                                    onClick={() =>
                                        handleSortSelection({
                                            direction: sortDirection,
                                            _sortBy: SortOption.DATES
                                        })
                                    }
                                >
                                    {t('classes.dates')}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                        <TableCell>{t('classes.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {status === Status.InProgress ? <LoadingTableRows /> : null}
                    {paginatedValues.map((classRecord) => (
                        <ClassTableRow key={classRecord.class_uuid} record={classRecord} />
                    ))}
                </TableBody>
            </StyledTable>
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>
            <DownloadChatModal
                open={!!selectedClass}
                onClose={() => setSelectedClass(null)}
                classId={selectedClass?.class_id}
                className={selectedClass?.class_name}
                userUuid={uuid}
            />
        </>
    );
};

export default ClassesTableBody;
